// The app's spine: one state object, and the store that lets things listen to it.
//
// Everything the client draws is a projection of this — the map layers, the
// chrome, the URL, localStorage. It used to sit at the top of app.ts, which is
// why every module that needed to read it had to live in app.ts too.
//
// `state` is still a plain object and still readable directly (`state.dateIdx`).
// What is new is `store.set({ dateIdx })`, which tells whoever asked to be told.
// Both work; the difference is whether anything downstream is listening. See
// src/lib/store.ts for why that matters.

import { createStore } from '../lib/store.ts';
import { DEFAULT_RECIPE, encodeRecipe } from '../lib/recipe.ts';
import { DEFAULT_BUCKET } from '../lib/buckets.ts';
import type { Recipe } from '../lib/recipe.ts';
import type { Manifest } from '../lib/manifest.ts';
import type { VarKey } from '../lib/variables.ts';
import type { CityProps, CitySeries, SelectedCity } from '../contracts/city.ts';
import type { FeatureCollection, Point } from 'geojson';

export type CityCollection = FeatureCollection<Point, CityProps>;

// Which screen is showing.
export type ViewName = 'landing' | 'map' | 'city' | 'compare';

// The day a "Find your day" rating was authored from: a place, a dekad and a
// time of day, plus the readings that were restated as the recipe. It names the
// rating in the sentence ("days like Split") and is the /day/… address — the
// day, not the camera. `canon` is the recipe it produced, so an edit after
// seeding is detectable (the URL then carries ?r= as well).
export interface DaySeed {
  id: number;
  name: string;
  country: string;
  placeSlug: string;
  lat: number;
  lon: number;
  dekad: string;
  bucket: string;
  canon: string;
  conditions: Record<string, number | null | undefined>;
}

// One place in a comparison, as much of it as whatever named it knew.
export interface ComparePlace {
  id: number;
  name: string;
  country: string;
  admin?: string; // a US city's state
  placeSlug: string;
  lat: number;
  lon: number;
}

// What the reader has unfolded on the "at a glance" page: rating rows opened
// into their places, charts split into one per place, the readings as a table.
// Persisted like whyExpanded — a reader who wants the table wants it next time.
export interface CompareUi {
  open: string[]; // rating keys whose leader row is open
  split: string[]; // overlay chart ids shown one per place
  table: boolean; // the readings as a table rather than dots
}

// The comparison: one to three places (one is a comparison waiting for its
// second), and which of the two documents is showing.
export interface CompareState {
  places: ComparePlace[];
  full: boolean;
}

// Which summoned surface is open, if any.
//
// One field rather than a flag per menu, and that is the point: the redesign's
// resting map is a sentence and a ruler, and everything else is asked for. Two
// of these open at once would already be the crowd of controls the header was
// deleted to remove — so the type makes it impossible rather than three
// booleans and a convention.
//
// The lens picker's three values are its two-step structure: 'lens' is the list
// of ratings, and each of the two rows ending in '›' swaps to its own step
// inside the same surface, with 'lens' as the way back.
//
// 'search' is the city search under its corner word (it doubles as the
// "Compare … with …" picker — `searchFor` says which); 'day-*' are the three
// words of the landing's "I loved … in … around …" sentence; 'compare-mode' is
// the "at a glance / in full" switch.
export type MenuName =
  | null | 'lens' | 'lens-custom' | 'lens-raw' | 'when' | 'bucket' | 'layers'
  | 'search' | 'day-place' | 'day-when' | 'day-bucket' | 'compare-mode' | 'compare-place';

// A shared read-only empty collection, for the style's initial source data —
// those are never annotated or handed to the store, so one instance is fine.
export const EMPTY_FC: CityCollection = { type: 'FeatureCollection', features: [] };

// A FRESH empty collection. The city path needs a new object each time: the
// store compares by identity, so reusing one instance would silently skip the
// legend update on a second consecutive empty frame — and annotateCustom writes
// into whatever it is given, which must never be a shared constant.
export const emptyCities = (): CityCollection => ({ type: 'FeatureCollection', features: [] });

// Shared water color: the ocean background and the ocean mask (which re-hides the
// field where it bleeds past coastlines) use the same value so water reads uniform.
export const OCEAN_COLOR = '#0a1626';
export const DEFAULT_FIELD_OPACITY = 0.6; // translucent wash so the land tint reads through

export interface AppState {
  manifest: Manifest | null;
  dates: string[];
  dateIdx: number;
  bucket: string;
  variable: VarKey;
  lastTemp: VarKey;
  lastWet: VarKey;
  playing: boolean;
  fps: number;
  version: string;
  fieldOn: boolean;
  fieldOpacity: number;
  citiesOn: boolean;
  citiesToken: number;
  cityFC: CityCollection;
  windOn: boolean;
  windToken: number;
  recipe: Recipe;
  recipeCanon: string;
  customOn: boolean;
  view: ViewName;
  selectedCity: SelectedCity | null;
  series: CitySeries | null;
  seriesToken: number;
  hoverId: number | null;
  menu: MenuName;
  peaksOpen: boolean;
  whyExpanded: boolean;
  daySeed: DaySeed | null;
  compare: CompareState;
  compareUi: CompareUi;
  // What the search panel's pick does: open a briefing, or add to a comparison.
  searchFor: 'open' | 'compare';
}

export const store = createStore<AppState>({
  manifest: null,
  dates: [],
  dateIdx: 0,
  bucket: DEFAULT_BUCKET,
  variable: 't',
  // The last-chosen member of each variant group, remembered independently of the
  // active variable so the tooltip can always show "the temperature view you picked"
  // and "the clouds & rain view you picked" — even while an Activity is selected.
  lastTemp: 't',
  lastWet: 'sky',
  playing: false,
  fps: 1 / 3, // one step every 3s — a calmer walk through the year (interval = 1000/fps)
  version: '',
  fieldOn: true, // smooth interpolated field visible
  fieldOpacity: DEFAULT_FIELD_OPACITY, // wash strength, driven by the opacity slider
  citiesOn: true, // city dots + labels
  citiesToken: 0, // guards async /cities fetches against races
  cityFC: EMPTY_FC, // the current frame's loaded city features, for the continuous-legend clip

  windOn: true, // wind arrows, over whichever field is selected
  windToken: 0, // guards async /wind fetches against races

  // The "My kind of day" recipe (see src/lib/recipe.ts). `recipe` is the clamped
  // object the evaluator takes; `recipeCanon` is its canonical wire string — the
  // exact string the tile URLs carry, so the browser cache and the server's
  // recipe cache key on the same spelling.
  recipe: DEFAULT_RECIPE,
  recipeCanon: encodeRecipe(DEFAULT_RECIPE),
  // Whether the custom score has been made the user's own — edited, selected,
  // or arrived at via a shared ?r= link. Until then the chip and its calendar
  // row stay hidden: the default recipe is a clone of an existing score, so an
  // un-tinkered "My kind of day" would just show duplicate data. Persisted, so once
  // seen it stays.
  customOn: false,

  // Redesign chrome: which screen is showing, and the city being briefed.
  view: 'landing', // landing | map | city
  selectedCity: null, // the open briefing's city, as much of it as the opener knew
  series: null, // the open city's typical-year series (from /city/:id/series)
  seriesToken: 0, // guards async series fetches against races
  hoverId: null, // id of the currently hovered city dot

  // Which summoned surface is open. Not persisted: a menu is transient, and a
  // reload that reopened one would be answering a question nobody asked.
  menu: null,

  // Whether "Where it peaks" is showing. Unlike a menu this IS remembered: the
  // panel is a place to work from rather than a question to answer, it survives
  // opening a city on purpose (so the next candidate is one click away), and a
  // reader who wants the shortlist beside the map wants it on the next visit too.
  peaksOpen: false,

  // Whether the score details are open. ONE preference across every transient
  // surface that states a score — the dot tooltip, the calendar's detail panel,
  // the peaks rows — because a reader who wants to see the reasons wants to see
  // them everywhere, and being asked again on each surface is the same question
  // three times. Persisted for the same reason. The briefing's masthead ignores
  // it and is always expanded: there the reasons ARE the page.
  whyExpanded: false,

  // The day behind "Find your day", when the custom rating came from one.
  // Persisted with the recipe it produced, so a reload still says whose day
  // the map is rating.
  daySeed: null,

  // The comparison. `full` is persisted like whyExpanded — a reader who wants
  // every reading wants it on the next comparison too — and the URL's /full
  // segment overrides it.
  compare: { places: [], full: false },
  compareUi: { open: [], split: [], table: false },
  searchFor: 'open',
});

// The live object, for the reads and the in-place writes that nothing listens to.
export const state = store.state;
